import type { Metadata } from "next";
import type { PublicContent } from "@/lib/content/public";
import { publicPagePath } from "@/lib/content/path";

const productionOrigin = process.env.NEXT_PUBLIC_SITE_URL || "https://dermalife.co.in";

export function decodeHtmlEntities(text: string | null | undefined): string {
  if (!text) return "";
  return text
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;|&#039;|&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function cleanMetaText(text: string | null | undefined): string | undefined {
  if (!text) return undefined;
  let decoded = decodeHtmlEntities(text);
  // Strip unresolved SEO plugin placeholders like #post_title, #site_title, %post_title%
  if (/^#post_title\b/i.test(decoded) || /^%post_title%\b/i.test(decoded) || decoded === "#post_title") {
    return undefined;
  }
  decoded = decoded.replace(/#site_title/gi, "").replace(/#sep/gi, "-").trim();
  decoded = decoded.replace(/^[-\s|]+|[-\s|]+$/g, "").trim();
  return decoded || undefined;
}

export function buildContentMetadata(content: PublicContent, canonicalPath: string): Metadata {
  const rawTitle = cleanMetaText(content.metaTitle) || cleanMetaText(content.title) || "Dermalife | Hair & Skin Clinic, New Delhi";
  const title = decodeHtmlEntities(rawTitle);
  const rawDescription = cleanMetaText(content.metaDescription) || cleanMetaText(content.excerpt) || undefined;
  const description = rawDescription ? decodeHtmlEntities(rawDescription) : undefined;
  const rawKeywords = cleanMetaText(content.metaKeywords);
  const keywords = rawKeywords ? decodeHtmlEntities(rawKeywords) : undefined;
  const canonical = new URL(publicPagePath(canonicalPath), productionOrigin).toString();
  const image = content.image ? new URL(content.image, productionOrigin).toString() : undefined;

  return {
    title,
    description,
    keywords,
    alternates: { canonical },
    openGraph: {
      type: content.type === "blog-post" ? "article" : "website",
      title,
      description,
      url: canonical,
      images: image ? [{ url: image }] : undefined,
    },
  };
}

export function medicalPageJsonLd(content: PublicContent, canonicalPath: string) {
  const title = decodeHtmlEntities(cleanMetaText(content.title) || "");
  const description = cleanMetaText(content.metaDescription) || cleanMetaText(content.excerpt) || undefined;
  return {
    "@context": "https://schema.org",
    "@type": content.type === "blog-post" ? "BlogPosting" : "MedicalWebPage",
    headline: title,
    description: description ? decodeHtmlEntities(description) : undefined,
    image: content.image ? new URL(content.image, productionOrigin).toString() : undefined,
    url: new URL(publicPagePath(canonicalPath), productionOrigin).toString(),
    publisher: {
      "@type": "MedicalClinic",
      name: "Dermalife Skin & Hair Clinic",
      url: productionOrigin,
      telephone: "+91-8882047047",
      address: {
        "@type": "PostalAddress",
        streetAddress: "C 13, Block C, Green Park Extension, Green Park",
        addressLocality: "New Delhi",
        postalCode: "110016",
        addressCountry: "IN",
      },
    },
  };
}

